import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { CardDataState } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import CardSkeleton, { CardSkeletonShape } from '../CardSkeleton';
import { isReasonCode, reasonCopyKey } from '../reasonKey';
import {
  DataStateIcon,
  DataStateLabel,
  MARKER_ICONS,
  MARKER_LABEL_KEYS,
  markerLabelKey,
  markerTone
} from './DataStateMarker';

export interface ICardDataStateBlockProps {
  state: CardDataState;
  /**
   * Technical Error only. `CardDataState` cannot carry a callback (it is payload-shaped), so the
   * retry affordance is wired by whoever owns the fetch.
   */
  onRetry?: () => void;
  /** Loading only — the body shape this card will settle into once it resolves. */
  skeletonShape?: CardSkeletonShape;
  /** Loading only — placeholder count passed through to the skeleton. */
  skeletonRows?: number;
  /**
   * Drawn above the marker, in the position the panel's own heading occupies when it is populated.
   * Pass it wherever this block stands in for a panel that owns no other heading — without it a
   * degraded panel is an unnamed marker and the reader cannot tell WHICH panel failed. Omitted (Layer
   * 1's cards, whose `InsightCard` frame draws the heading itself) the block is exactly what it was.
   */
  title?: string;
  subtitle?: string;
  /**
   * Draws the state's own sentence under its label. OFF by default: Layer 1's 27 shipped cards draw
   * the label alone, and this must not move their pixels. PLA-8828.
   */
  describes?: boolean;
  className?: string;
}

const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${tokens.space['24']} ${tokens.space['16']};
  text-align: center;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). The doubled class
     outranks the children's own margin reset, which is injected later and would otherwise win the
     cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

/* The heading keeps the panel's own left-aligned position at the top of the box while the marker
   below it stays centred, so a degraded panel reads as the same panel rather than as a different
   one. Stretched to the block's full width because the block itself is a centred column. */
const Heading = styled.header`
  align-self: stretch;
  padding-bottom: ${tokens.space['8']};
  text-align: left;

  && > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

// IntelligenceContentScope colours every h1-h6 through a descendant selector, which outranks a
// single class.
const HeadingTitle = styled.h4`
  margin: 0;
  font-size: ${tokens.type['card-title-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['card-title-line']};

  && {
    color: ${tokens.color['text-title']};
  }
`;

const HeadingSubtitle = styled.p`
  margin: 0;
  color: ${tokens.color['text-caption']};
  font-size: ${tokens.type['card-description-size']};
  font-weight: ${tokens.type['card-description-weight']};
  line-height: ${tokens.type['card-description-line']};
`;

const Detail = styled.p`
  max-width: 420px;
  margin: 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const RetryButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.color['accent-blue']};
  cursor: pointer;
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const REASON_GROUP = `${TCustomerInsights}.DataStateReasons`;

/**
 * The only text this block may add beyond its label from the PAYLOAD, and only when the payload
 * supplies it: an absent reason stays absent rather than becoming a guess at why the card is
 * degraded. The catalogue sentence under `describes` is the general fallback behind it.
 *
 * `assembleCard` fills `reason` from `reason_code`, falling back to `calculation_status` — two
 * pipeline vocabularies with no discriminator between them, which is why one flat group answers for
 * both. A code with no copy yet draws NOTHING: `Insufficient Data - governed_non_scalar_grain` on a
 * customer-facing card is the pipeline's private language, not an explanation. Prose is passed
 * through, because a payload that already speaks English is already the sentence. PLA-8828.
 */
const detailOf = (state: CardDataState): string | undefined => {
  if (state.kind !== 'insufficientData' || !state.reason) {
    return undefined;
  }

  return isReasonCode(state.reason) ? undefined : state.reason;
};

const reasonKeyOf = (state: CardDataState): string | null =>
  state.kind === 'insufficientData' && state.reason
    ? reasonCopyKey(REASON_GROUP, state.reason)
    : null;

const CardDataStateBlock: React.FC<ICardDataStateBlockProps> = ({
  state,
  onRetry,
  skeletonShape,
  skeletonRows,
  title,
  subtitle,
  describes = false,
  className
}) => {
  const { t } = useMultiLanguageTranslation();

  if (state.kind === 'ok') {
    return null;
  }

  if (state.kind === 'loading') {
    return (
      <CardSkeleton shape={skeletonShape || 'kpis'} rows={skeletonRows} className={className} />
    );
  }

  const MarkerIcon = MARKER_ICONS[state.kind];
  const labelKey = markerLabelKey(
    state.kind,
    state.kind === 'insufficientData' ? state.reason : undefined
  );
  // The detail fallback stays on the KIND's key: `DataStateDetails` is a sentence per state, and a
  // reason that renames the heading has already supplied its own sentence below. PLA-8828.
  // `assembleCard` names the unservable readings when it can, so the specific sentence is preferred
  // over the general one; either only appears where the caller opted in.
  const describedKey =
    state.kind === 'notConfigured' && state.missing
      ? 'NotConfiguredMissing'
      : MARKER_LABEL_KEYS[state.kind];
  // The payload's own reason first, translated where the code has copy; the state's general
  // sentence only where the caller opted in and nothing more specific was said.
  const reasonKey = reasonKeyOf(state);
  const detail =
    (reasonKey ? t(reasonKey) : detailOf(state)) ||
    (describes
      ? t(`${TCustomerInsights}.DataStateDetails.${describedKey}`, {
          missing: state.kind === 'notConfigured' ? state.missing : undefined
        })
      : undefined);
  const canRetry = state.kind === 'technicalError' && Boolean(onRetry);

  return (
    <Block
      className={className}
      data-testid="card-data-state-block"
      data-state={state.kind}
      // `status` on every kind, and no explicit `aria-live`: the role already implies `polite`, and
      // `alert`/`assertive` is for a time-sensitive change the user must act on — this block mounts
      // already carrying its text, so an assertive marker would interrupt the page on first paint.
      role="status"
    >
      {title ? (
        <Heading data-testid="card-data-state-block-heading">
          <HeadingTitle data-testid="card-data-state-block-title">{title}</HeadingTitle>
          {subtitle ? (
            <HeadingSubtitle data-testid="card-data-state-block-subtitle">
              {subtitle}
            </HeadingSubtitle>
          ) : null}
        </Heading>
      ) : null}

      <DataStateIcon
        aria-hidden
        data-testid="card-data-state-block-icon"
        data-tone={markerTone(state.kind)}
      >
        <MarkerIcon />
      </DataStateIcon>

      <DataStateLabel as="p" data-testid="card-data-state-block-label">
        {t(`${TCustomerInsights}.DataStates.${labelKey}`)}
      </DataStateLabel>

      {detail ? <Detail data-testid="card-data-state-block-detail">{detail}</Detail> : null}

      {canRetry ? (
        <RetryButton type="button" onClick={onRetry} data-testid="card-data-state-block-retry">
          {t(`${TCustomerInsights}.Actions.Retry`)}
        </RetryButton>
      ) : null}
    </Block>
  );
};

export default CardDataStateBlock;
